import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

// ── AI Chatbot per-user credit quotas (KOA-18066) ──────────────────────────
// Owner-facing quota surface backed by Backend `v1/api/AIWorkspace/quota/*`
// (AIWorkspaceController). Every mutating call is domain-owner-gated server
// side; GetQuotaOverview returns the whole picture in one request so the UI
// never re-implements window math (weekly = Monday 00:00 UTC, monthly =
// calendar month UTC — computed server-side).

function rethrowOrHandle(error: unknown): never {
  if (Axios.isCancel(error)) {
    throw error;
  }
  KMainFunctions.exceptionHandler(error);
  throw error;
}

const backendUrl: string = CommonHelperFunctions.GetBackendUrl();

export type AIQuotaExceedAction = "block" | "warn";

export type AIUserQuotaStatus = "ok" | "warning" | "blocked" | "unlimited";

/** One stored quota policy row. `userId` null = the workspace default row. */
export interface AIQuotaPolicy {
  userId: string | null;
  weeklyLimitCredits: number | null;
  monthlyLimitCredits: number | null;
  isUnlimited: boolean;
  exceedAction: AIQuotaExceedAction | null;
}

export interface AIQuotaDefaultUpdate {
  weeklyLimitCredits: number | null;
  monthlyLimitCredits: number | null;
  exceedAction: AIQuotaExceedAction;
}

export interface AIQuotaOverrideUpdate {
  weeklyLimitCredits: number | null;
  monthlyLimitCredits: number | null;
  isUnlimited: boolean;
}

/** One workspace member with consumption + server-resolved effective policy. */
export interface AIQuotaUserRow {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePhotoUrl: string | null;
  isDomainOwner: boolean;
  weeklyConsumedCredits: number;
  monthlyConsumedCredits: number;
  override: AIQuotaPolicy | null;
  effectiveWeeklyLimitCredits: number | null;
  effectiveMonthlyLimitCredits: number | null;
  effectiveIsUnlimited: boolean;
  effectiveExceedAction: AIQuotaExceedAction;
  status: AIUserQuotaStatus;
}

export interface AIQuotaConfigResponse {
  defaultPolicy: AIQuotaPolicy | null;
  users: AIQuotaUserRow[];
  weekWindowStartUtc: string;
  monthWindowStartUtc: string;
}

export interface AIWorkspaceMeResponse {
  userId: string;
  domainId: string;
  isDomainOwner: boolean;
}

export class AiQuotaService {
  /** Full quota picture: default policy + every member's usage/override/status. */
  public static async GetQuotaOverview(signal?: AbortSignal): Promise<AIQuotaConfigResponse> {
    try {
      const res = await Axios.get<AIQuotaConfigResponse>(`${backendUrl}/AIWorkspace/quota/config`, { signal });
      return res.data;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }

  public static async UpdateWorkspaceQuotaConfig(update: AIQuotaDefaultUpdate): Promise<AIQuotaPolicy> {
    const res = await Axios.put<AIQuotaPolicy>(`${backendUrl}/AIWorkspace/quota/default`, update);
    return res.data;
  }

  public static async UpsertUserQuotaOverride(userId: string, override: AIQuotaOverrideUpdate): Promise<AIQuotaPolicy> {
    const res = await Axios.put<AIQuotaPolicy>(
      `${backendUrl}/AIWorkspace/quota/users/${encodeURIComponent(userId)}`,
      override
    );
    return res.data;
  }

  public static async DeleteUserQuotaOverride(userId: string): Promise<void> {
    await Axios.delete(`${backendUrl}/AIWorkspace/quota/users/${encodeURIComponent(userId)}`);
  }

  /** Identity/authority snapshot — used for owner-only tab visibility fallbacks. */
  public static async GetMe(signal?: AbortSignal): Promise<AIWorkspaceMeResponse> {
    try {
      const res = await Axios.get<AIWorkspaceMeResponse>(`${backendUrl}/AIWorkspace/me`, { signal });
      return res.data;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }
}
